import { hasIn } from 'lodash';

import {
  PagePermission,
  Portal2Permissions,
  Portal2PortalRole,
  Portal2Role,
  RulesPermission,
  SetupPage,
  SetupPagePermission,
  SetupPermission,
  TabPermission,
} from 'src/app/core/interfaces/permissions';
import { ResourceModeConst, ServiceModeConst } from 'src/app/core/constants';
import { Rule } from 'src/app/core';

export abstract class PermissionsC {
  public static portal = 'portal';

  constructor() {}

  private static _generateScope(res: string): string {
    return res.replace(`${this.portal}.`, '').replace(/\./g, ':');
  }

  public static generatePortal2PortalRole(
    id: string,
    title: string,
    roles: Portal2Role[]
  ): Portal2PortalRole {
    const prefix: string = `${this.portal}.portal2.${id}`;
    const adminPrefix: string = `${this.portal}.portal2.global.${id}`;
    const p: Portal2PortalRole = {
      id: adminPrefix,
      title: title,
      roles,
    };

    p.roles.map((role: Portal2Role, index: number) => {
      const roleId = `${prefix}.${role.id}`;
      role.id = roleId;
      if (hasIn(role.permissions, 'rules')) {
        role.permissions.rules.push({
          mode: ResourceModeConst.VIEW,
          res: roleId,
        });

        role.permissions.rules.push({
          mode: ResourceModeConst.VIEW,
          res: adminPrefix,
        });
        return role;
      }

      return role;
    });

    return p;
  }

  public static setupPages({
    pageLabels,
    productLineLabel,
    hidePath = false, // hide the path like the administrative path
  }: {
    pageLabels: string[];
    productLineLabel: string;
    hidePath?: boolean; // hide the path like the administrative path
  }): SetupPermission {
    const pages = {};
    const productLine: SetupPermission = PermissionsC.getSetupPermission(
      productLineLabel,
      hidePath
    );

    pageLabels.forEach((setupPage: string) => {
      pages[this.kabobCaseToCamelCase(setupPage)] =
        this.generateSetupPagePermission(setupPage, productLine);
    });

    return {
      ...productLine,
      pages,
    };
  }

  /**
   * Generate SetupPagePermission
   * @param path
   * @param productLine
   * @param addPageRules
   */
  public static generateSetupPagePermission(
    path: string,
    productLine: SetupPermission
  ): SetupPage {
    let subPath: string;
    const resName = this.kabobCaseToCamelCase(path);

    if (typeof productLine.path === 'undefined') {
      subPath = 'administrative';
    } else {
      subPath = productLine.path;
    }

    // const res = `${this.portal}.page.${this.kabobCaseToCamelCase(
    //   subPath
    // )}.${resName}`;

    const res = `${this.portal}.${this.kabobCaseToCamelCase(
      subPath
    )}.${resName}`;

    const setupPage: SetupPage = {
      id: res,
      absolutePath: hasIn(productLine, 'path')
        ? `/${productLine.path}/${path}`
        : `/${path}`,
      relativePath: path,
    };

    return setupPage;
  }

  /**
   * Generate PagePermission
   * @param parent
   * @param title
   * @param permissions
   */
  public static generatePagePermission(
    parent: SetupPage,
    title: string,
    permissions?: TabPermission[],
    resList?: string[]
  ): PagePermission {
    const res = parent.id;

    const pagePermission: PagePermission = {
      id: res,
      scope: this._generateScope(res),
      title,
      absolutePath: parent.absolutePath,
      relativePath: parent.relativePath,
    };

    if (Array.isArray(permissions)) {
      pagePermission.tabs = permissions;
    }

    if (resList && resList.length) {
      let resourceList = {};

      resList.forEach((r) => {
        resourceList[`${this.resourceNameToCamelCase(r)}`] = `${res}.${r}`;
      });

      pagePermission.resList = resourceList;
    }

    return pagePermission;
  }

  /**
   * Generate TabPermission
   * @param parent
   * @param path
   * @param title
   * @param rules
   */
  public static generateTabPermission(
    parent: SetupPage,
    path: string,
    title: string,
    resList: string[] = []
  ): TabPermission {
    const res = `${parent.id}.${this.kabobCaseToCamelCase(path)}`;

    const tabPermission: TabPermission = {
      id: res,
      scope: this._generateScope(res),
      title: title,
      absolutePath: `${parent.absolutePath}/${path}`,
      relativePath: path,
    };

    if (resList && resList.length) {
      let resourceList = {};

      resList.forEach((r) => {
        resourceList[`${this.resourceNameToCamelCase(r)}`] = `${res}.${r}`;
      });

      tabPermission.resList = resourceList;
    }

    return tabPermission;
  }

  /**
   * Get setup permission
   * @param path
   */
  public static getSetupPermission(
    path: string,
    hidePath = false // hide the path like the administrative path
  ): SetupPermission {
    const id = `portal.${this.kabobCaseToCamelCase(path)}`;
    // const id = `portal.productLine.${this.kabobCaseToCamelCase(path)}`;

    const setupPermission: SetupPermission = {
      id,
    };

    if (!hidePath) setupPermission.path = path;

    return setupPermission;
  }

  public static resourceNameToCamelCase(resourceName: string) {
    return this.kabobCaseToCamelCase(this._replaceAll(resourceName, '.', '-'));
  }

  private static _replaceAll(str, find, replace) {
    return str.replace(new RegExp(this._escapeRegExp(find), 'g'), replace);
  }

  private static _escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  /**
   * Convert kabob case to camel case
   * @param kabobCase
   */
  public static kabobCaseToCamelCase(kabobCase: string): string {
    const symbol = '-';

    if (kabobCase.includes(symbol)) {
      const wordArray = kabobCase.split(symbol);

      const upperCaseWrods = wordArray.map((word: string, index: number) => {
        if (index !== 0) {
          return word[0].toUpperCase() + word.substring(1);
        }

        return word;
      });

      return upperCaseWrods.join('');
    }

    return kabobCase;
  }

  /**
   * Set permission setup
   * @param productLine
   * @param pages
   */
  public static setupPermission(
    productLine: SetupPermission,
    pages: SetupPagePermission
  ): SetupPermission {
    return {
      id: productLine.id,
      pages,
    };
  }
}
